<template>
	<OnboardingFormStep :commit="commit" class="narrow">

		<template #heading>
			<h1 class="onboarding-step-heading">I've been doing this professionally for...</h1>
		</template>

		<div class="grid gap-20">
			<div v-for="(text, index) in options" :key="index" class="radio">
				<input v-model="experience" :id="`experience${index}`" :value="index" type="radio" required>
				<label :for="`experience${index}`">{{ text }}</label>
			</div>
		</div>
		
		<p class="field-error" v-if="error">{{ error }}</p>
	</OnboardingFormStep>
</template>

<script>
import { toInteger } from '@/helpers/convert';
import onboarding from '@/mixins/onboarding';

const maxValue = 6;

/**
 * @param {unknown} experience
 * @returns {experience is number}
 */
function isValidExperience(experience) {
  return Number.isSafeInteger(experience) && experience >= 0 && experience <= maxValue;
}

export default {
  name: 'OnboardingExperience',
  mixins: [onboarding],
  onboardingStep: {
    isApplicable: ({ plan }) => plan && plan.canBeHired,
    isCompleted: ({ profile }) => isValidExperience(profile?.experience),
    requires: ({ profile }) => profile,
  },
  data() {
    return {
      /** @type {string | null} */
      error: null,
    };
  },
  computed: {
    experience: {
      /**
       * @returns {number | null}
       */
      get() {
        /** @type {import('@/mixins/onboarding').OnboardingComponent} */
        const { profile } = this;
        return profile ? profile.experience : null;
      },
      /**
       * @param {number | null} value
       * @returns {void}
       */
      set(value) {
        /** @type {import('@/mixins/onboarding').OnboardingComponent} */
        const { profile } = this;
        if (profile) {
          profile.experience = value;
        }
      },
    },
    /**
     * @returns {readonly string[]}
     */
    options() {
      /** @type {string[]} */
      const options = ['Less than 1 year'];
      let value = 0;
      while (++value < maxValue) {
        options[value] = `${value} year${value === 1 ? '' : 's'}`;
      }
      options[value] = `${value} or more years`;
      return Object.freeze(options);
    },
  },
  methods: {
    /**
     * @returns {Promise<boolean>}
     */
    async commit() {
      const experience = toInteger(this.experience, 0, maxValue);
      if (!isValidExperience(experience)) {
        this.error = 'Please indicate your level of experience.';
        return false;
      }
      await this.$store.dispatch('profiles/updateActiveProfile', { experience });
      this.$gtmDispatchOnboardingStepEvent(4, 'Years of Experience Input');
      return true;
    },
  },
  watch: {
    experience() {
      this.error = null;
    },
  },
};
</script>
